import { APIComponentAttributes, ArdoqId } from '@ardoq/api-types';
import { isTruthy } from '@ardoq/common-helpers';
import { toIds } from '@ardoq/core';
import { componentOrDescendantsAreIncludedInContextByFilter } from '@ardoq/component-interface';
import {
  GraphGroup,
  GraphNode,
  RawGraphNode2,
  TraversalAndGroupingResult,
  LoadedGraphWithViewpointMode,
  pruneToStartSetBranches,
} from '@ardoq/graph';
import {
  TimelineViewGraphHierarchy,
  getViewGraphHierarchyFromTraversal,
  createViewGraphHierarchy,
} from '@ardoq/timeline2024';

import { uniq } from 'lodash';
import { getGraphShapeForTraversalHierarchy } from 'modelInterface/graph/graphShapeForTraversalHierarchy';

type ComponentRawGraphItem = Omit<RawGraphNode2, 'modelId'> & {
  modelId: string;
};

export const getComponentGroupRawItems = (
  groupMap: Map<string, RawGraphNode2>,
  componentIds: ReadonlySet<ArdoqId>
) =>
  Array.from(groupMap.values()).filter((item): item is ComponentRawGraphItem =>
    Boolean(item.modelId && componentIds.has(item.modelId))
  );

export const rootGroupsAndStartSetToViewGraphHierarchy = (
  rootGroups: RawGraphNode2[],
  startSet: string[],
  componentMap: Map<string, RawGraphNode2>
) => {
  const rootGroupModelIds = new Set(
    rootGroups.map(({ modelId }) => modelId).filter(isTruthy)
  );

  const ungroupedStartSetNodes = startSet
    .map(entityId => componentMap.get(entityId))
    .filter((rawItem): rawItem is RawGraphNode2 =>
      Boolean(
        rawItem &&
        !rawItem.parentId &&
        rawItem.modelId &&
        !rootGroupModelIds.has(rawItem.modelId)
      )
    );

  const commonHierarchy = rootGroups.concat(ungroupedStartSetNodes);

  const { viewGraphHierarchy } = commonHierarchy.reduce(
    createViewGraphHierarchy(),
    {
      viewGraphHierarchy: new Map(),
      usedNodes: new Map(),
    }
  );

  return viewGraphHierarchy;
};

type GetTimelineViewGraphHierarchyForViewpointModeArgs = {
  loadedGraph: LoadedGraphWithViewpointMode;
  hasUserDefinedGroups: boolean;
  traversalAndGroupingResult: TraversalAndGroupingResult;
  allComponentIds: ArdoqId[];
  shouldShapeHierarchyByTraversal: boolean;
};

type ToTraversalShapedRootItemsArgs = {
  rootItems: RawGraphNode2[];
  startSet: ArdoqId[];
  traversalAndGroupingResult: TraversalAndGroupingResult;
  shouldShapeHierarchyByTraversal: boolean;
};
const toTraversalShapedRootItems = ({
  rootItems,
  startSet,
  traversalAndGroupingResult,
  shouldShapeHierarchyByTraversal,
}: ToTraversalShapedRootItemsArgs): RawGraphNode2[] => {
  if (!shouldShapeHierarchyByTraversal) {
    return rootItems;
  }
  return pruneToStartSetBranches({
    rootItems,
    startSet,
    componentMap: traversalAndGroupingResult.componentMap,
    groupMap: traversalAndGroupingResult.groupMap,
  }).rootItems;
};
/**
 * Default behavior is to group by traversal steps and the traversal root nodes should be grouped by parent-all.
 *
 * Currently (not final), if user defines other grouping rules, it would break the traversal hierarchy =>
 * We create the view graph hierarchy either from the user defined grouping OR the the loadedGraph =>
 * if hasUserDefinedGrouping - use it, otherwise getViewGraphHierarchyFromTraversal.
 */
export const getTimelineViewGraphHierarchyForViewpointMode = ({
  loadedGraph,
  hasUserDefinedGroups,
  traversalAndGroupingResult,
  allComponentIds,
  shouldShapeHierarchyByTraversal,
}: GetTimelineViewGraphHierarchyForViewpointModeArgs) => {
  const { rootGroups, componentMap } = traversalAndGroupingResult;

  if (hasUserDefinedGroups) {
    return {
      viewGraphHierarchy: rootGroupsAndStartSetToViewGraphHierarchy(
        rootGroups,
        allComponentIds,
        componentMap
      ),
    };
  }

  const startSet = uniq(
    loadedGraph.hierarchyDefinition.flatMap(
      ({ startSetResult }) => startSetResult
    )
  );

  const { graphAsHierarchy, rootItems } = getGraphShapeForTraversalHierarchy({
    loadedGraph,
    traversalAndGroupingResult,
    startSet,
  });

  const viewGraphHierarchy = getViewGraphHierarchyFromTraversal({
    graphAsHierarchy,
    rootItems: toTraversalShapedRootItems({
      rootItems,
      startSet,
      traversalAndGroupingResult,
      shouldShapeHierarchyByTraversal,
    }),
    omitLinksToGroupingAncestors: shouldShapeHierarchyByTraversal,
  });

  return {
    viewGraphHierarchy,
  };
};

const createOrGetGraphNodeForComponent = (
  entityId: ArdoqId,
  usedNodesByEntityId: Map<ArdoqId, GraphNode>
) => {
  const existingGraphNode = usedNodesByEntityId.get(entityId);
  if (existingGraphNode) {
    return existingGraphNode;
  }
  const newGraphNode = GraphNode.create(entityId);
  usedNodesByEntityId.set(entityId, newGraphNode);
  return newGraphNode;
};

type UngroupedComponentsToViewGraphHierarchyReducerState = {
  viewGraphHierarchy: TimelineViewGraphHierarchy;
  componentMap: Map<string, RawGraphNode2>;
  childrenByParent: Map<ArdoqId, APIComponentAttributes[]>;
  allComponentIds?: string[];
  usedGraphNodes?: Map<ArdoqId, GraphNode>;
  groupedNodes?: Map<string, GraphNode | GraphGroup>;
};
/**
 * This function is called to reduce the startSet of components when
 * - we are in workspace mode
 * - there is no grouping applied
 *
 * This means, the same entity can not have 2 graph nodes.
 */
export const ungroupedComponentsToViewGraphHierarchyReducer =
  (parentGraphNodeId?: string) =>
  (
    state: UngroupedComponentsToViewGraphHierarchyReducerState,
    entityId: ArdoqId
  ): UngroupedComponentsToViewGraphHierarchyReducerState => {
    const {
      viewGraphHierarchy,
      allComponentIds,
      usedGraphNodes = new Map<ArdoqId, GraphNode>(),
      groupedNodes,
      componentMap,
      childrenByParent,
    } = state;

    if (groupedNodes?.has(entityId)) {
      return state;
    }

    /**
     * We are reusing nodes because the child nodes are created from the parent node,
     * so that we can add the viewGraphHierarchy entry with graph items and children.
     */
    const graphNode = createOrGetGraphNodeForComponent(
      entityId,
      usedGraphNodes
    );
    allComponentIds?.push(entityId);

    const childrenIds = toIds(childrenByParent.get(entityId) ?? []).filter(
      componentOrDescendantsAreIncludedInContextByFilter
    );
    const childNodes = childrenIds.map(childId =>
      createOrGetGraphNodeForComponent(childId, usedGraphNodes)
    );

    const rawGraphItem: RawGraphNode2 = componentMap.get(entityId) || {
      id: entityId,
      modelId: entityId,
    };

    viewGraphHierarchy.set(graphNode.id, {
      children: childNodes,
      parentGraphNodeId,
      graphItem: graphNode,
      rawGraphItem: rawGraphItem,
    });

    return childrenIds?.length
      ? childrenIds.reduce(
          ungroupedComponentsToViewGraphHierarchyReducer(graphNode.id),
          {
            viewGraphHierarchy,
            componentMap,
            childrenByParent,
            allComponentIds,
            usedGraphNodes,
            groupedNodes,
          }
        )
      : state;
  };

type GetTimelineViewGraphHierarchyForWorkspaceModeArgs = {
  startSet: ArdoqId[];
  hasUserDefinedGroups: boolean;
  traversalAndGroupingResult: TraversalAndGroupingResult;
  childrenByParent: Map<ArdoqId, APIComponentAttributes[]>;
};

export const getTimelineViewGraphHierarchyForWorkspaceMode = ({
  startSet,
  hasUserDefinedGroups,
  traversalAndGroupingResult,
  childrenByParent,
}: GetTimelineViewGraphHierarchyForWorkspaceModeArgs): {
  viewGraphHierarchy: TimelineViewGraphHierarchy;
  allComponentIds: ArdoqId[];
} => {
  const { groupMap, rootGroups, componentMap } = traversalAndGroupingResult;

  if (hasUserDefinedGroups) {
    const componentGroupModelIds = getComponentGroupRawItems(
      groupMap,
      new Set(componentMap.keys())
    ).map(({ modelId }) => modelId);

    const allComponentIds = [...componentMap.keys(), ...componentGroupModelIds];

    return {
      viewGraphHierarchy: rootGroupsAndStartSetToViewGraphHierarchy(
        rootGroups,
        startSet,
        componentMap
      ),
      allComponentIds,
    };
  }

  const { allComponentIds, viewGraphHierarchy } = startSet.reduce(
    ungroupedComponentsToViewGraphHierarchyReducer(),
    {
      viewGraphHierarchy: new Map(),
      allComponentIds: [],
      componentMap,
      childrenByParent,
    }
  );

  return {
    viewGraphHierarchy,
    allComponentIds: allComponentIds ?? [],
  };
};
